import * as Yup from 'yup'
import {
  transformNumberValue,
  transformNumberValueAllowZero,
} from '../../../shared/transformNumberValue'
import { IEmissionFactors } from './types'
import { testNumberWithDotLength } from '../../../shared/testNumberWithDotLength'

export const formSchema = Yup.object().shape({
  productType: Yup.number()
    .typeError('Field accepts numbers only')
    .min(0, 'Please select product type')
    .required()
    .integer(),
  productId: Yup.number()
    .typeError('Field accepts numbers only')
    .min(0, 'Please select product type')
    .required()
    .integer(),
  productNameAtSource: Yup.string()
    .max(200, 'Max 200 characters')
    .required('Product Name At Source is a required field'),
  numberOfStudies: Yup.number()
    .transform(transformNumberValue)
    .typeError('Field accepts numbers only')
    .positive('Field value should be greater than 0!')
    .integer(),
  numberOfCycles: Yup.number()
    .transform(transformNumberValue)
    .typeError('Field accepts numbers only')
    .positive('Field value should be greater than 0!')
    .integer(),
  countryId: Yup.number().typeError('Field accepts numbers only').integer().nullable(true),
  referenceSourcingCountry: Yup.number()
    .typeError('Field accepts numbers only')
    .integer()
    .nullable(true),
  regionIdentifierId: Yup.number()
    .typeError('Please select region')
    .min(1, 'Please select region')
    .required('Please select region')
    .integer(),
  referenceRegionIdentifier: Yup.number()
    .typeError('Field accepts numbers only')
    .min(1, 'Please select region')
    .required()
    .integer(),
  productionSystem: Yup.number().when('productType', {
    is: (val: number) => val == 0,
    then: Yup.number().min(1, 'Please select production system').required(),
    otherwise: Yup.number().nullable(true),
  }),
  plantProtectionTypeGroup: Yup.number().when('productType', {
    is: (val: number) => val == 0,
    then: Yup.number().min(1, 'Please select plant protection type group').required(),
    otherwise: Yup.number().nullable(true),
  }),
  functionalUnit: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .min(0, 'Please select unit')
    .required('Functional Unit is a required field')
    .integer(),
  coreVersionRelease: Yup.string()
    .nullable(true)
    .max(256, 'Reference Technology Type field maximum length is 256 characters!'),
  calculatorVersion: Yup.string().when('dataOrigin', {
    is: (val: number) => val == 1 || val == 2,
    then: Yup.string().nullable(true),
    otherwise: Yup.string().nullable(true),
  }),
  farmDataCalculator: Yup.string().when('dataOrigin', {
    is: (val: number) => val == 1 || val == 2,
    then: Yup.string().required('Farm Data Calculator is a required field').nullable(true),
    otherwise: Yup.string().nullable(true),
  }),
  pnNExcelCalculator: Yup.number().when('dataOrigin', {
    is: (val: number) => val == 1 || val == 2,
    then: Yup.number()
      .transform(transformNumberValueAllowZero)
      .typeError('Field accepts numbers only')
      .min(0, 'Please select excel calculator')
      .required('PnN Excel Calculator is a required field')
      .nullable(true),
    otherwise: Yup.number().nullable(true),
  }),
  dataOrigin: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .min(0, 'Please select data origin')
    .required('Please select data origin')
    .integer(),
  externalSource: Yup.string().when('dataOrigin', {
    is: (val: number) => val == 3,
    then: Yup.string().required('External Source field is required').nullable(true),
    otherwise: Yup.string().nullable(true),
  }),
  postHarvestLossesId: Yup.number()
    .nullable(true)
    .when('functionalUnit', {
      is: (val: number) => val == 0 || val == 5 || val == 6,
      then: Yup.number()
        .nullable(true)
        .transform(transformNumberValueAllowZero)
        //.required('Post Harvest Losses Id field is required')
        .nullable(true),
      otherwise: Yup.number().nullable(true),
    }),
  postHarvestLosses: Yup.number()
    .nullable(true)
    .when('functionalUnit', {
      is: (val: number) => val == 0 || val == 5 || val == 6,
      then: Yup.number()
        .nullable(true)
        //.required('Post Harvest Losses field is required')
        .min(0, 'Number should be greater or equal 0')
        .max(100, 'Number should be less or equal 100'),
      otherwise: Yup.number().nullable(true),
    }),
  landUse: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .required('Land use is required')
    .min(0, 'Number should be greater or equal 0')
    .test('len', 'Max symbols allowed - 32. Max symbols after dot - 16', testNumberWithDotLength),
  biodiversity: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .min(0, 'Number should be greater or equal 0')
    .test('len', 'Max symbols allowed - 32. Max symbols after dot - 16', testNumberWithDotLength),
  biodiversityPDFYear: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .min(0, 'Number should be greater or equal 0')
    .test('len', 'Max symbols allowed - 32. Max symbols after dot - 16', testNumberWithDotLength),
  carbon: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .required('Carbon is required')
    .test('len', 'Max symbols allowed - 32. Max symbols after dot - 16', testNumberWithDotLength),
  eutrophication: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .required('Eutrophication is required')
    .min(0, 'Number should be greater or equal 0')
    .test('len', 'Max symbols allowed - 32. Max symbols after dot - 16', testNumberWithDotLength),
  waterUsage: Yup.number().when('productType', {
    is: (val: number) => val == 3,
    then: Yup.number()
      .transform(transformNumberValueAllowZero)
      .typeError('Field accepts numbers only')
      .required('Water usage is required')
      .test('len', 'Max symbols allowed - 32. Max symbols after dot - 16', testNumberWithDotLength),
    otherwise: Yup.number()
      .transform(transformNumberValueAllowZero)
      .typeError('Field accepts numbers only')
      .required('Water usage is required')
      .min(0, 'Number should be greater or equal 0')
      .test('len', 'Max symbols allowed - 32. Max symbols after dot - 16', testNumberWithDotLength),
  }),
  waterUsageIncludingWasterScarcity: Yup.number().when('productType', {
    is: (val: number) => val == 3,
    then: Yup.number()
      .transform(transformNumberValueAllowZero)
      .typeError('Field accepts numbers only')
      .required('Field is required')
      .test('len', 'Max symbols allowed - 32. Max symbols after dot - 16', testNumberWithDotLength),
    otherwise: Yup.number()
      .transform(transformNumberValueAllowZero)
      .typeError('Field accepts numbers only')
      .required('Field is required')
      .min(0, 'Number should be greater or equal 0')
      .test('len', 'Max symbols allowed - 32. Max symbols after dot - 16', testNumberWithDotLength),
  }),
  referencesSamplingStartYear: Yup.number()
    .typeError('Field accepts numbers only')
    .min(1900, 'Year can`t be less than 1900')
    .max(2200, 'Year can`t be greater than 2200')
    .nullable(true),
  referencesSamplingYear: Yup.number()
    .typeError('Field accepts numbers only')
    .min(1900, 'Year can`t be less than 1900')
    .max(2200, 'Year can`t be greater than 2200')
    .nullable(true),
  secondaryDataReferenceSourceType: Yup.number()
    .typeError('Field accepts numbers only')
    .min(0, 'Please select sources')
    .required()
    .integer(),
  referenceDataPublicationYear: Yup.number()
    .transform(transformNumberValue)
    .typeError('Field accepts numbers only')
    .min(1900, 'Year can`t be less than 1900')
    .max(2200, 'Year can`t be greater than 2200')
    .required('Reference data publication year field is required!'),
  referenceTechnologyType: Yup.string()
    .max(256, 'Reference Technology Type field maximum length is 256 characters!')
    .nullable(true),
  referenceTechnologyProxyContribution: Yup.number()
    .typeError('Please enter value between 0 and 100')
    .min(0, 'Value should be between 0 and 100')
    .max(100, 'Value should be between 0 and 100')
    .test('len', 'Max symbols after dot - 16', testNumberWithDotLength)
    .nullable(true),
  referenceSourcingCountryProxyContribution: Yup.number()
    .typeError('Field accepts numbers only')
    .min(0, 'Value should be between 0 and 100')
    .max(100, 'Value should be between 0 and 100')
    .test('len', 'Max symbols after dot - 16', testNumberWithDotLength)
    .nullable(true),
  flag_L1_VerifiedRemovalsCarbon: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .nullable(true),
  flag_L1_UnverifiedRemovalsCarbon: Yup.number()
    .transform(transformNumberValueAllowZero)
    .typeError('Field accepts numbers only')
    .nullable(true),
  impacts: Yup.object().shape({
    abioticDepletion: Yup.number().transform(transformNumberValueAllowZero).nullable(true),
    abioticDepletionFossilFuels: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    abioticResourceDepletionFossilFuels: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    acidification: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    aboveGroundCropResidueBurnt: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    aboveGroundCropResidueLeftOnField: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    aboveGroundCropResidueRemoved: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    biogenicCarbonDioxide: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    biogenicMethane: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    biogenicProductEmissions: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    belowGroundCropResidue: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    capturedBiogenicCO2WithGeologicStorage: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    carbonDioxide: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    completenessCropResidue: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    completenessElectricityFuel: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    completenessFertiliser: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    completenessOtherChemical: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    completenessPesticideVetinaryDrug: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    completenessProduct: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    completenessSeed: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    completenessWater: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    cumulativeEnergyDemand: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    cumulativeEnergyDemand_NonRenewable: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    cumulativeEnergyDemand_Renewable: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    damageToTerrestrialEcosystems_LandOccupation: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    damageToTerrestrialEcosystems_LandTransformation: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    deforestation: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    diesel: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    economicValueShare: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    electricity: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    ecotoxicityFreshwater: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    fluorinatedGHGs: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    fossilCarbonDioxide: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    fossilMethane: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    fugitiveEmissions: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    eutrophicationTerrestrial: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    freshwaterEcotoxicity: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    freshwaterEutrophication: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    ghgTotal_EF_PEF_EmissionsMinusRemovals: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    hfCs: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    humanCarcinogenicToxicity: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    humanNonCarcinogenicToxicity: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    humanToxicityCancer: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    humanToxicityNonCancer: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    inorganicFertiliser_K2O: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    inorganicFertiliser_N: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    inorganicFertiliser_P2O5: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    inputsProduction: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    irrigation: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    ionisingRadiation: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    kynetecProduceID: Yup.string().nullable(true),
    landCover: Yup.string().nullable(true),
    landEmissions: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    landManagementCO2Removals: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    landManagementNetBiogenicCO2Emissions: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    landManagementProductionEmissions: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    landTransformation: Yup.string().nullable(true),
    lime: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    marineEcotoxicity: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    marineEutrophication: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    methane: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    mineralsMetalsUse: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    mobileCombustion: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    montrealProtocolSubstance_IndustrialProcesses: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    montrealProtocolSubstance_InputsProduction: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    nF3: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    nitrousOxide: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    organicFertiliser_K2O: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    organicFertiliser_N: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    organicFertiliser_P2O5: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    otherFluorinatedGHG_IndustrialProcesses: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    otherFluorinatedGHG_InputsProduction: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    outsideLSRS: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    pfCs: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    particulateMatter: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    pesticide: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    photochemicalOzoneFormation: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    processEmissions: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope1: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope1_CH4: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope1_CO2: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope1_HFCs: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope1_N2O: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope1_NF3: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope1_PFCs: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope1_SF6: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope2: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope2_CH4: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope2_CO2: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope2_HFCs: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope2_N2O: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope2_NF3: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope2_PFCs: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope2_SF6: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope3: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope3_CH4: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope3_CO2: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope3_HFCs: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope3_N2O: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope3_NF3: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope3_PFCs: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope3_SF6: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    scope3Cat1_PurchasedGoodsServices: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    scope3Cat3_FuelEnergyRelatedActivities: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    sF6: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    seed: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    siteType: Yup.string().nullable(true),
    soilQualityIndex_TotalLandUseEffects: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    stationaryCombustion: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    stratosphericOzoneDepletion: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    terrestrialEcotoxicity: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
    technologicalCO2RemovalsWithGeologicStorage: Yup.number()
      .nullable(true)
      .transform(transformNumberValueAllowZero),
    yield: Yup.number().nullable(true).transform(transformNumberValueAllowZero),
  }),
})

export const copyToClipboard = (value: string) => {
  const anyNavigator: any = window.navigator
  anyNavigator.clipboard.writeText(value)
}

export enum TabsEnum {
  PRODUCT = 'PRODUCT',
  DATA_ORIGIN = 'DATA_ORIGIN',
  EMISSIONS = 'EMISSIONS',
  IMPACTS = 'IMPACTS',
  ACTIVITY_DATA = 'ACTIVITY_DATA',
  LOSSES = 'LOSSES',
  DATA_QUALITY = 'DATA_QUALITY',
}

export const FLAG_FIELDS = [
  'flag_L1_LandManagementCarbon',
  'flag_L1_LandManagementNonCarbon',
  'flag_L1_LandUseChangeCarbon',
  'flag_L1_NonLandCarbon',
]

export const TAB_LABELS = {
  [TabsEnum.PRODUCT]: 'Product/produce info',
  [TabsEnum.DATA_ORIGIN]: 'Data origin',
  [TabsEnum.EMISSIONS]: 'GHG Emissions',
  [TabsEnum.IMPACTS]: 'Impacts',
  [TabsEnum.ACTIVITY_DATA]: 'Activity Data',
  [TabsEnum.LOSSES]: 'Losses',
  [TabsEnum.DATA_QUALITY]: 'Data quality',
}

export const TAB_ERRORS = {
  [TabsEnum.PRODUCT]: [
    'productType',
    'productId',
    'productNameAtSource',
    'countryId',
    'regionIdentifierId',
    'productionSystem',
    'plantProtectionTypeGroup',
    'functionalUnit',
    'impacts.kynetecProduceID',
    'impacts.landTransformation',
    'impacts.deforestation',
    'impacts.siteType',
    'impacts.landCover',
  ],
  [TabsEnum.DATA_ORIGIN]: [
    'coreVersionRelease',
    'calculatorVersion',
    'farmDataCalculator',
    'pnNExcelCalculator',
    'dataOrigin',
    'externalSource',
  ],
  [TabsEnum.EMISSIONS]: [
    'carbon',
    'flag_L1_UnverifiedRemovalsCarbon',
    'flag_L1_VerifiedRemovalsCarbon',
    'impacts.ghgTotal_EF_PEF_EmissionsMinusRemovals',
    'impacts.stationaryCombustion',
    'impacts.mobileCombustion',
    'impacts.processEmissions',
    'impacts.fugitiveEmissions',
    'impacts.landEmissions',
    'impacts.landManagementNetBiogenicCO2Emissions',
    'impacts.landManagementProductionEmissions',
    'impacts.biogenicProductEmissions',
    'impacts.inputsProduction',
    'impacts.carbonDioxide',
    'impacts.fossilCarbonDioxide',
    'impacts.biogenicCarbonDioxide',
    'impacts.methane',
    'impacts.fossilMethane',
    'impacts.biogenicMethane',
    'impacts.nitrousOxide',
    'impacts.fluorinatedGHGs',
    'impacts.hFCs',
    'impacts.pFCs',
    'impacts.sF6',
    'impacts.nF3',
    'impacts.scope1',
    'impacts.scope1_CO2',
    'impacts.scope1_CH4',
    'impacts.scope1_N2O',
    'impacts.scope1_HFCs',
    'impacts.scope1_PFCs',
    'impacts.scope1_SF6',
    'impacts.scope1_NF3',
    'impacts.scope2',
    'impacts.scope2_CO2',
    'impacts.scope2_CH4',
    'impacts.scope2_N2O',
    'impacts.scope2_HFCs',
    'impacts.scope2_PFCs',
    'impacts.scope2_SF6',
    'impacts.scope2_NF3',
    'impacts.scope3',
    'impacts.scope3_CO2',
    'impacts.scope3_CH4',
    'impacts.scope3_N2O',
    'impacts.scope3_HFCs',
    'impacts.scope3_PFCs',
    'impacts.scope3_SF6',
    'impacts.scope3_NF3',
    'impacts.scope3Cat1_PurchasedGoodsServices',
    'impacts.scope3Cat3_FuelEnergyRelatedActivities',
    'impacts.landManagementCO2Removals',
    'impacts.capturedBiogenicCO2WithGeologicStorage',
    'impacts.technologicalCO2RemovalsWithGeologicStorage',
    'impacts.outsideLSRS',
    'impacts.montrealProtocolSubstance_IndustrialProcesses',
    'impacts.montrealProtocolSubstance_InputsProduction',
    'impacts.otherFluorinatedGHG_IndustrialProcesses',
    'impacts.otherFluorinatedGHG_InputsProduction',
  ],
  [TabsEnum.IMPACTS]: [
    'landUse',
    'biodiversity',
    'biodiversityPDFYear',
    'eutrophication',
    'waterUsage',
    'waterUsageIncludingWasterScarcity',
    'waterUsageIncludingWasterScarcity',
    'impacts.abioticDepletion',
    'impacts.abioticDepletion',
    'impacts.abioticDepletionFossilFuels',
    'impacts.acidification',
    'impacts.ecotoxicityFreshwater',
    'impacts.eutrophicationTerrestrial',
    'impacts.freshwaterEcotoxicity',
    'impacts.freshwaterEutrophication',
    'impacts.humanCarcinogenicToxicity',
    'impacts.humanNonCarcinogenicToxicity',
    'impacts.humanToxicityCancer',
    'impacts.humanToxicityNonCancer',
    'impacts.ionisingRadiation',
    'impacts.marineEcotoxicity',
    'impacts.marineEutrophication',
    'impacts.particulateMatter',
    'impacts.photochemicalOzoneFormation',
    'impacts.stratosphericOzoneDepletion',
    'impacts.terrestrialEcotoxicity',
    'impacts.damageToTerrestrialEcosystems_LandOccupation',
    'impacts.damageToTerrestrialEcosystems_LandTransformation',
    'impacts.soilQualityIndex_TotalLandUseEffects',
    'impacts.cumulativeEnergyDemand',
    'impacts.cumulativeEnergyDemand_NonRenewable',
    'impacts.cumulativeEnergyDemand_Renewable',
    'impacts.abioticResourceDepletionFossilFuels',
    'impacts.mineralsMetalsUse',
  ],
  [TabsEnum.ACTIVITY_DATA]: [
    'impacts.yield',
    'impacts.economicValueShare',
    'impacts.aboveGroundCropResidueLeftOnField',
    'impacts.aboveGroundCropResidueRemoved',
    'impacts.aboveGroundCropResidueBurnt',
    'impacts.belowGroundCropResidue',
    'impacts.seed',
    'impacts.inorganicFertiliser_N',
    'impacts.inorganicFertiliser_P2O5',
    'impacts.inorganicFertiliser_K2O',
    'impacts.organicFertiliser_N',
    'impacts.organicFertiliser_P2O5',
    'impacts.organicFertiliser_K2O',
    'impacts.lime',
    'impacts.pesticide',
    'impacts.diesel',
    'impacts.electricity',
    'impacts.irrigation',
  ],
  [TabsEnum.LOSSES]: ['postHarvestLossesId', 'postHarvestLosses'],
  [TabsEnum.DATA_QUALITY]: [
    'referencesSamplingStartYear',
    'referencesSamplingYear',
    'secondaryDataReferenceSourceType',
    'referenceDataPublicationYear',
    'referenceSourcingCountry',
    'referenceRegionIdentifier',
    'referenceTechnologyType',
    'referenceTechnologyProxyContribution',
    'referenceSourcingCountryProxyContribution',
    'numberOfStudies',
    'numberOfCycles',
    'impacts.completenessCropResidue',
    'impacts.completenessElectricityFuel',
    'impacts.completenessFertiliser',
    'impacts.completenessOtherChemical',
    'impacts.completenessPesticideVetinaryDrug',
    'impacts.completenessProduct',
    'impacts.completenessSeed',
    'impacts.completenessWater',
  ],
}

export const getFLAGCarbonValue = (data: IEmissionFactors) =>
  FLAG_FIELDS.every(item => data[item] === null)
    ? null
    : FLAG_FIELDS.reduce((acc, field) => {
        const fieldValue = data[field]

        if (!fieldValue) {
          return acc
        }

        const value = parseFloat(fieldValue)

        return value ? Number((acc + value).toPrecision(12)) : acc
      }, 0)
